import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Collapse from "@mui/material/Collapse";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Fab from "@mui/material/Fab";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { isEmpty, checkIfPublicHoliday } from "../../../app/utils/helpers";

const ORANGE = "#FF9015";

function getHoursLabel(rowData) {
    if (rowData.from && rowData.to) {
        const fromDate = new Date(rowData.from);
        const toDate = new Date(rowData.to);
        return ((toDate.getTime() - fromDate.getTime()) / (60 * 60000)).toFixed(1);
    }
    return "-";
}

function formatTimeFromTimestamp(timestamp) {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
}

function getSelectedDayPeriod(dateTime, heureMoyenne, heureDebut, heureFin) {
    const date = new Date(dateTime);
    const hour = date.getHours();
    if (hour < heureMoyenne) return heureDebut;
    else if (hour > heureMoyenne) return heureFin;
    return heureMoyenne;
}

export default function MobileTimesheetDaily({
    modifiedPlanning,
    selectedPlanning,
    hasModifications,
    teams,
    teamsList,
    groupesList,
    collabs,
    timesheetSampleShift,
    settings,
    userRoleId,
    myTeam,
    holidays,
    onNormalDayCheckChange,
    onTypeChange,
    onFromTimeChange,
    onToTimeChange,
    onFromDayPeriodChange,
    onToDayPeriodChange,
    onClientChange,
    onGroupChange,
    onDescriptionChange,
    onAddRow,
    onSave,
}) {
    const [expandedIds, setExpandedIds] = useState({});

    let heureDebut = 9, heureFin = 17, heureMoyenne = 13;
    Object.values(settings.settingsList).map(item => {
        if (item.label === "Heure début de journée") heureDebut = Number(item.settingValue);
        if (item.label === "Heure fin de journée") heureFin = Number(item.settingValue);
        heureMoyenne = (heureFin + heureDebut) / 2;
    });

    function isDisabled(rowData) {
        return false;
    }

    function checkIfNormalDay(rowData) {
        return !isEmpty(rowData.id_sample_shift);
    }

    function checkIfRowDisabled(rowData) {
        return isDisabled(rowData) || !checkIfNormalDay(rowData);
    }

    function getAvailableShiftTypes() {
        return [
            { value: "s", label: Localize("Journée standard"), roleId: 3 }
        ].concat(
            (settings.shiftTypes || []).filter(st => userRoleId <= st.roleId)
        );
    }

    function getFromDayPeriodOptions() {
        return [
            { value: heureDebut, label: Localize("Matin") },
            { value: heureMoyenne, label: Localize("Midi") },
        ];
    }

    function getToDayPeriodOptions() {
        return [
            { value: heureMoyenne, label: Localize("Midi") },
            { value: heureFin, label: Localize("Soir") },
        ];
    }

    function toggleExpand(id) {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    }

    function isWeekend(rowData) {
        const date = moment(rowData.dateTime, "x");
        return date.day() === 0 || date.day() === 6;
    }

    function isHoliday(rowData) {
        const date = moment(rowData.dateTime, "x");
        const weekDay = date.day();
        if (weekDay === 0 || weekDay === 6) return false;
        return checkIfPublicHoliday(
            holidays && holidays[date.year()] ? holidays[date.year()] : null,
            date,
            myTeam ? myTeam.id_country : null
        );
    }

    function getCardBorderColor(rowData) {
        if (isWeekend(rowData)) return "#ef5350";
        if (isHoliday(rowData)) return "#ff9800";
        return "transparent";
    }

    function getCardBgColor(rowData) {
        if (isWeekend(rowData)) return "rgba(239, 83, 80, 0.04)";
        if (isHoliday(rowData)) return "rgba(255, 152, 0, 0.04)";
        return "var(--surface-card)";
    }

    const selectSx = {
        fontSize: "16px",
        minHeight: 44,
        "& .MuiSelect-select": { fontSize: "16px", py: 1.25 },
    };

    return (
        <Box sx={{ pb: 12, px: 1, mt: 0, overflowY: "auto", height: "calc(100vh - 56px)", WebkitOverflowScrolling: "touch" }}>
            {modifiedPlanning && modifiedPlanning.map((rowData, rowIndex) => {
                const expanded = !!expandedIds[rowData.id];
                const isNormalDay = checkIfNormalDay(rowData);
                const disabled = checkIfRowDisabled(rowData);
                const hours = getHoursLabel(rowData);
                const borderColor = getCardBorderColor(rowData);
                const bgColor = getCardBgColor(rowData);
                const isWeekendDay = isWeekend(rowData);
                const isHolidayDay = isHoliday(rowData);

                const teamId = rowData.id_team_delegate ? rowData.id_team_delegate : rowData.id_team;
                const collab = collabs.collaboratorsList
                    ? collabs.collaboratorsList.filter(c => c.id === rowData.id_collab)[0]
                    : null;
                const filteredGroupes = groupesList
                    ? groupesList.filter(g => collab && collab.groupeIds && collab.groupeIds.includes(g.id))
                    : [];

                return (
                    <Card
                        key={rowData.id}
                        sx={{
                            mb: 1,
                            borderRadius: 2,
                            borderLeft: `4px solid ${borderColor}`,
                            backgroundColor: bgColor,
                            boxShadow: expanded ? 3 : 1,
                            transition: "box-shadow 0.2s ease-in-out",
                            "&:active": { transform: "scale(0.995)" },
                        }}
                    >
                        {/* Card Header */}
                        <Box
                            onClick={() => toggleExpand(rowData.id)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 1.5,
                                py: 1,
                                cursor: "pointer",
                                minHeight: 56,
                                userSelect: "none",
                            }}
                        >
                            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: isWeekendDay ? "#ef5350" : isHolidayDay ? "#ff9800" : "text.primary",
                                    }}
                                >
                                    {rowData.day}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {rowData.date}
                                </Typography>
                            </Box>

                            {/* Work day toggle */}
                            <Switch
                                size="small"
                                checked={isNormalDay}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onNormalDayCheckChange({ value: rowData.id, checked: e.target.checked });
                                }}
                                disabled={isDisabled(rowData)}
                                sx={{
                                    mx: 0.5,
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: ORANGE },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: ORANGE },
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />

                            {/* Hours badge */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mr: 0.5,
                                    minWidth: 50,
                                    justifyContent: "flex-end",
                                }}
                            >
                                <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: hours !== "-" ? ORANGE : "text.disabled" }}
                                >
                                    {hours}h
                                </Typography>
                            </Box>

                            {/* Expand icon */}
                            <IconButton
                                size="small"
                                sx={{
                                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.2s ease-in-out",
                                }}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        </Box>

                        {/* Expandable Content */}
                        <Collapse in={expanded} timeout={250}>
                            <Divider />
                            <CardContent sx={{ px: 1.5, py: 1.5, "&:last-child": { pb: 1.5 } }}>
                                {/* Type */}
                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <InputLabel sx={{ fontSize: "14px" }}>
                                        <Langs str="Type" />
                                    </InputLabel>
                                    <Select
                                        label={Localize("Type")}
                                        value={rowData.type || ""}
                                        disabled={disabled}
                                        onChange={(e) => onTypeChange({ target: { id: rowData.id, value: e.target.value } })}
                                        sx={selectSx}
                                    >
                                        {getAvailableShiftTypes().map(st => (
                                            <MenuItem key={st.value} value={st.value} sx={{ fontSize: "16px", minHeight: 44 }}>
                                                {st.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Time inputs row */}
                                <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                                    {rowData.type === "s" ? (
                                        <>
                                            {/* Period selectors for standard day */}
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: "14px" }}>
                                                    <Langs str="Start" />
                                                </InputLabel>
                                                <Select
                                                    label={Localize("Start")}
                                                    value={
                                                        rowData.from
                                                            ? getSelectedDayPeriod(rowData.from, heureMoyenne, heureDebut, heureFin)
                                                            : ""
                                                    }
                                                    disabled={disabled || !rowData.from}
                                                    onChange={(e) => {
                                                        const option = getFromDayPeriodOptions().find(o => o.value === e.target.value);
                                                        if (option) onFromDayPeriodChange(option, rowData.id);
                                                    }}
                                                    sx={selectSx}
                                                >
                                                    {getFromDayPeriodOptions().map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "16px", minHeight: 44 }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: "14px" }}>
                                                    <Langs str="End" />
                                                </InputLabel>
                                                <Select
                                                    label={Localize("End")}
                                                    value={
                                                        rowData.to
                                                            ? getSelectedDayPeriod(rowData.to, heureMoyenne, heureDebut, heureFin)
                                                            : ""
                                                    }
                                                    disabled={disabled || !rowData.from}
                                                    onChange={(e) => {
                                                        const option = getToDayPeriodOptions().find(o => o.value === e.target.value);
                                                        if (option) onToDayPeriodChange(option, rowData.id);
                                                    }}
                                                    sx={selectSx}
                                                >
                                                    {getToDayPeriodOptions().map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "16px", minHeight: 44 }}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </>
                                    ) : (
                                        <>
                                            {/* Time pickers for non-standard day */}
                                            <TextField
                                                type="time"
                                                label={<Langs str="Start" />}
                                                size="small"
                                                fullWidth
                                                disabled={disabled || !rowData.from}
                                                value={formatTimeFromTimestamp(rowData.from)}
                                                onChange={(e) => {
                                                    const [h, m] = e.target.value.split(":").map(Number);
                                                    onFromTimeChange(h * 60 + m, rowData.id);
                                                }}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: "14px" } }}
                                                inputProps={{
                                                    style: { fontSize: "16px", minHeight: 28 },
                                                    max: formatTimeFromTimestamp(rowData.to),
                                                }}
                                            />
                                            <TextField
                                                type="time"
                                                label={<Langs str="End" />}
                                                size="small"
                                                fullWidth
                                                disabled={disabled || !rowData.from}
                                                value={formatTimeFromTimestamp(rowData.to)}
                                                onChange={(e) => {
                                                    const [h, m] = e.target.value.split(":").map(Number);
                                                    onToTimeChange(h * 60 + m, rowData.id);
                                                }}
                                                InputLabelProps={{ shrink: true, sx: { fontSize: "14px" } }}
                                                inputProps={{
                                                    style: { fontSize: "16px", minHeight: 28 },
                                                    min: formatTimeFromTimestamp(rowData.from),
                                                }}
                                            />
                                        </>
                                    )}
                                </Box>

                                {/* Client dropdown */}
                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <InputLabel sx={{ fontSize: "14px" }}>
                                        <Langs str="Client" />
                                    </InputLabel>
                                    <Select
                                        label={Localize("Client")}
                                        value={teamId || ""}
                                        disabled={disabled}
                                        onChange={(e) => onClientChange({ target: { id: rowData.id, value: e.target.value } })}
                                        sx={selectSx}
                                    >
                                        {teams && teams.map(t => (
                                            <MenuItem key={t.value} value={t.value} sx={{ fontSize: "16px", minHeight: 44 }}>
                                                {t.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Group dropdown */}
                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <InputLabel sx={{ fontSize: "14px" }}>
                                        <Langs str="Group" />
                                    </InputLabel>
                                    <Select
                                        label={Localize("Group")}
                                        value={rowData.id_groupe || ""}
                                        disabled={disabled}
                                        onChange={(e) => onGroupChange({ target: { id: rowData.id, value: e.target.value } })}
                                        sx={selectSx}
                                    >
                                        {filteredGroupes.map(g => (
                                            <MenuItem key={g.id} value={g.id} sx={{ fontSize: "16px", minHeight: 44 }}>
                                                {g.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Comments */}
                                <TextField
                                    label={<Langs str="Comments" />}
                                    multiline
                                    minRows={2}
                                    maxRows={4}
                                    fullWidth
                                    size="small"
                                    disabled={disabled}
                                    value={rowData.description || ""}
                                    onChange={(e) => onDescriptionChange({ target: { id: rowData.id, value: e.target.value } })}
                                    inputProps={{ maxLength: 200, style: { fontSize: "16px" } }}
                                    InputLabelProps={{ sx: { fontSize: "14px" } }}
                                    sx={{ mb: 1 }}
                                />

                                {/* Add row button */}
                                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onAddRow(rowData, { rowIndex })}
                                        sx={{
                                            color: ORANGE,
                                            border: `1px solid ${ORANGE}`,
                                            borderRadius: 2,
                                            minWidth: 44,
                                            minHeight: 44,
                                            "&:active": { transform: "scale(0.95)" },
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                        <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600, color: ORANGE }}>
                                            <Langs str="ADD_SHIFT" />
                                        </Typography>
                                    </IconButton>
                                </Box>
                            </CardContent>
                        </Collapse>
                    </Card>
                );
            })}

            {/* Save FAB */}
            {hasModifications && (
                <Fab
                    onClick={onSave}
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        backgroundColor: ORANGE,
                        color: "#fff",
                        zIndex: 1400,
                        "&:hover": { backgroundColor: "#e8820d" },
                        "&:active": { transform: "scale(0.95)" },
                        width: 56,
                        height: 56,
                    }}
                >
                    <SaveIcon />
                </Fab>
            )}
        </Box>
    );
}
